import type { DesignListItem } from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { formatDate } from '@/shared/lib';
import { DesignThumbnail } from '@/shared/ui';

interface MyDesignCardProps {
  readonly design: DesignListItem;
  /** 카드 아래 버튼 줄 — 수정·견적 요청 등은 조합하는 쪽이 넣는다. */
  readonly actions?: ReactNode;
  /** 이름 줄 오른쪽의 작은 도구(복제·삭제 아이콘) — 조합하는 쪽이 넣는다. */
  readonly tools?: ReactNode;
  /** 이름 자리에 넣을 요소(인라인 이름 편집기 등). 없으면 이름 글자. */
  readonly title?: ReactNode;
}

/**
 * 저장한 디자인 카드 — 상품 카드와 같은 각진 회색 정사각 칸에 서버 썸네일을 놓고, 아래에 이름 한 줄·(상품명 ·) 수정일 한 줄.
 * 썸네일이 없거나(준비 중) 저장본과 다르면 서버가 null 을 준다 — 그때와 그림을 불러오지 못했을 때는 "미리보기 준비 중".
 */
export const MyDesignCard = ({
  design,
  actions,
  tools,
  title,
}: MyDesignCardProps): ReactElement => (
  <article aria-label={design.name} className="flex flex-col gap-4">
    <DesignThumbnail url={design.thumbnail?.url ?? null} alt={`${design.name} 미리보기`} />
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="text-[15px] leading-snug font-semibold wrap-anywhere">
          {title ?? design.name}
        </h3>
        {/* 기본 디자인 이름은 상품명이다 — 다를 때만 날짜 앞에 상품명을 붙여 카드 높이를 한 줄로 맞춘다. */}
        <p className="text-xs text-muted-foreground tabular-nums wrap-anywhere">
          {design.productName !== design.name ? `${design.productName} · ` : ''}
          {formatDate(design.updatedAt)} 수정
        </p>
      </div>
      {tools ? <div className="flex shrink-0 items-center gap-0.5">{tools}</div> : null}
    </div>
    {actions}
  </article>
);
